import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trophy, UserCheck } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api, arabicError } from "../lib/api";
import { PeriodBar, Kpi, Section, Empty } from "./accounting/ui";
import { money, num, meters, dateAr } from "./accounting/format";

const today = () => new Date().toISOString().slice(0, 10);

const EmployeeCard = ({ e, rank }) => (
  <div className="bg-white border border-[#DCEBEE] rounded-2xl p-4 shadow-sm" data-testid={`emp-perf-${e.actor_id}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className={`w-8 h-8 rounded-full font-extrabold text-sm flex items-center justify-center shrink-0 ${rank === 0 && e.sales_count > 0 ? "bg-amber-100 text-amber-700" : "bg-[#EBF4F6] text-[#09637E]"}`}>{rank + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-[#333333] truncate">{e.name}</div>
        <div className="text-[11px] text-[#777777]">{e.actor_type === "merchant" ? "صاحب المتجر" : e.is_active ? "موظف مفعّل" : "موظف موقوف/سابق"} · آخر نشاط: {dateAr(e.last_activity)}</div>
      </div>
      {e.sales_count === 0 && e.returns_processed === 0 && <span className="text-[11px] font-bold bg-gray-100 text-gray-600 rounded px-2 py-0.5">بلا نشاط</span>}
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[["مبيعاته", money(e.sales_amount), "text-[#09637E]", "sales"], ["عدد البيعات", num(e.sales_count), "text-[#333333]", "count"], ["الكمية", meters(e.qty_sold), "text-[#333333]", "qty"],
        ["متوسط البيع", money(e.avg_sale_value), "text-[#088395]", "avg"], ["رجّع (نفّذ)", `${num(e.returns_processed)} · ${money(e.returned_value)}`, "text-rose-600", "returns"], ["مرتجع على بيعاته", `${num(e.returns_on_own_sales)} · ${money(e.returned_value_on_own_sales)}`, "text-amber-600", "own-returns"]].map(([l, v, c, t]) => (
        <div key={t} className="bg-[#F5FAFB] rounded-xl p-2" data-testid={`emp-perf-${e.actor_id}-${t}`}>
          <div className="text-[10px] text-[#777777] font-bold leading-tight">{l}</div>
          <div className={`text-sm md:text-base font-extrabold tabular-nums mt-0.5 ${c}`} dir="ltr">{v}</div>
        </div>
      ))}
    </div>
  </div>
);

export default function EmployeePerformance() {
  const [period, setPeriod] = useState("week");
  const [anchor, setAnchor] = useState(today());
  const [custom, setCustom] = useState({ start: today(), end: today() });
  const [data, setData] = useState(null);
  const params = useMemo(() => (period === "custom" ? { period, start: custom.start, end: custom.end } : { period, date: anchor }), [period, anchor, custom]);
  useEffect(() => {
    api.get("/accounting/employees", { params }).then((r) => setData(r.data)).catch((e) => toast.error(arabicError(e)));
  }, [params]);
  const rows = data?.employees || [];
  const active = rows.filter((r) => r.sales_count > 0 || r.returns_processed > 0);
  const chart = active.slice(0, 8).map((r) => ({ name: r.name.length > 10 ? r.name.slice(0, 10) + "…" : r.name, sales: r.sales_amount, returns: r.returned_value }));
  return (
    <div className="space-y-4 md:space-y-6" data-testid="employee-performance">
      <PeriodBar period={period} setPeriod={(p) => { setPeriod(p); if (p !== "custom") setAnchor(today()); }} anchor={anchor} setAnchor={setAnchor} range={data?.range} custom={custom} setCustom={setCustom} />
      {!data ? <div className="text-center text-[#666666] p-6">جارٍ التحميل...</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Kpi label="مبيعات فيزيائية (الفريق)" value={money(data.totals.sales_amount)} testid="emp-total-sales" sub={`${num(data.totals.sales_count)} عملية`} />
            <Kpi label="الأكثر بيعاً" value={active[0]?.sales_count ? active[0].name : "—"} tone="green" testid="emp-top-seller" sub={active[0]?.sales_count ? money(active[0].sales_amount) : undefined} />
            <Kpi label="الأكثر تنفيذاً للمرتجعات" value={[...active].sort((a, b) => b.returns_processed - a.returns_processed)[0]?.returns_processed ? [...active].sort((a, b) => b.returns_processed - a.returns_processed)[0].name : "—"} tone="amber" testid="emp-top-returner" sub={`${num(data.totals.returns_processed)} إرجاع إجمالاً`} />
            <Kpi label="موظفون نشِطون" value={`${num(active.filter((r) => r.actor_type === "employee").length)} / ${num(rows.filter((r) => r.actor_type === "employee").length)}`} tone="teal" testid="emp-active" sub="ضمن الفترة" />
          </div>
          {chart.length > 1 && (
            <Section title="مقارنة سريعة: مَن باع ومَن رجّع" icon={Trophy} testid="emp-chart">
              <div className="h-48 w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#666" }} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: "#666" }} />
                    <Tooltip formatter={(v, n) => [money(v), n === "sales" ? "باع" : "رجّع (قيمة)"]} />
                    <Bar dataKey="sales" fill="#09637E" radius={[6, 6, 0, 0]} maxBarSize={36} />
                    <Bar dataKey="returns" fill="#E11D48" radius={[6, 6, 0, 0]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          )}
          <Section title="أداء كل موظف" icon={UserCheck} testid="emp-cards">
            {rows.length === 0 ? <Empty text="لا يوجد موظفون" /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">{rows.map((e, i) => <EmployeeCard key={e.actor_id} e={e} rank={i} />)}</div>
            )}
            <div className="text-[11px] text-[#888888] mt-4">«رجّع (نفّذ)» = عمليات الإرجاع التي سجّلها هذا الشخص، و«مرتجع على بيعاته» = مرتجعات على عمليات باعها هو (قد يكون نفّذها شخص آخر). الإرجاع ليس مؤشراً سلبياً بحد ذاته. المبيعات الأونلاين ({money(data.totals.online_sales)}) تعود للمتجر ولا تُنسب لموظف.</div>
          </Section>
        </>
      )}
    </div>
  );
}
